/**
 * The two desks of arranging: the bars as they were written, and as they were arranged.
 *
 * Side by side is the point of the lab's board, and there is only room for it from `xl`. Narrower
 * than that the two desks used to stack, and a stack of two full scores fits no window: each
 * was a page and a half tall, so the reader scrolled to find the music and scrolled again to
 * reach its play button. Below `xl` one desk is shown at a time instead, with a switch over
 * it, and the one shown has the whole height of the board -- which is what a page fitted to
 * its desk needs. The switch opens on the arrangement once there is one, since that is what
 * the run was for, and goes back to it on every new run. Arrange has its three questions beside
 * the desks at every width, so it shows one desk at a time always (`beside` off), under a
 * switch a finger tall.
 *
 * Both desks stay mounted whichever is shown, so the one hidden keeps its place in the music
 * and is heard where it was left when it is switched back to. The space bar plays the desk
 * that last played, or else the one in front: the arrangement where both are.
 */

import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import type { Score } from "@app/score-editor";
import type { ArrangePayload, ArrangeStart } from "@app/shared-types";
import ScoreAudition from "./ScoreAudition";
import { count } from "../lib/format";
import type { Original } from "../lib/score-window";
import { DRAWN_BARS } from "../lib/score-window";
import { useStage } from "../lib/use-stage";

/** The two panels the board mounts, in the order they are drawn, for :func:`useStage`. */
const PANELS = ["original", "arranged"] as const;

type Desk = (typeof PANELS)[number];

/** What a desk says before it has a score on it. Sized like the panel it stands in for. */
function Empty({ children }: { children: ReactNode }): JSX.Element {
  return (
    <div className="flex min-h-[220px] flex-1 items-center justify-center rounded-md border border-dashed border-shell-seam bg-canvas/40 p-8 text-center xl:min-h-0">
      <p className="max-w-sm text-[12px] leading-relaxed text-shell-dim">{children}</p>
    </div>
  );
}

/** What the lab's empty desk says: what is still missing, or what a run from `start` will do. */
export function labWaiting(picked: boolean, rostered: boolean, start: ArrangeStart): string {
  if (!picked) return "The arrangement will be drawn beside the original, to be played against it.";
  if (!rostered) {
    return "Choose who is playing. Pick a named ensemble or add players one at a time — nothing is arranged for a roster you did not ask for.";
  }
  if (start === "tune") {
    return "Press Arrange. The passage is arranged anew from its tune and chords: the tune as the score has it, and every other part written again under it.";
  }
  if (start === "model") {
    return "Press Arrange. Only the tune is kept, as the score has it: the note model writes the parts under it, and the check holds what it wrote to the book's rules.";
  }
  return "Press Arrange. Each player is given the source's own notes — a whole part of it where there is a player for every part, or else the tune, the bass or the notes between — and rests wherever the source gives them nothing; only an octave may change.";
}

export interface ArrangeDesksProps {
  /** The library score being fetched, by id, or `null` where none was asked for. */
  readonly wanted: string | null;
  readonly fetching: boolean;
  readonly source: Original | null;
  /** What the arrangement's desk says before there is one. */
  readonly waiting: string;
  /** Both desks side by side from `xl`, or one at a time at every width. */
  readonly beside: boolean;
  readonly result: ArrangePayload | null;
  /** The arrangement as this browser read it: the whole of it, and the bars drawn. */
  readonly read: { readonly score: Score | null; readonly reason: string | null };
  readonly arranged: Score | null;
  onOpenInEditor(musicxml: string): void;
}

export default function ArrangeDesks({
  wanted,
  fetching,
  source,
  waiting,
  beside,
  result,
  read,
  arranged,
  onOpenInEditor,
}: ArrangeDesksProps): JSX.Element {
  const [originalStage, arrangedStage] = useStage(PANELS);
  const [chosen, setChosen] = useState<Desk | null>(null);
  useEffect(() => setChosen(null), [result]);
  const front: Desk = chosen ?? (result === null ? "original" : "arranged");
  const spaceTo: Desk = originalStage.held ? "original" : arrangedStage.held ? "arranged" : front;
  const shown = (desk: Desk): string =>
    `${desk === front ? "flex" : "hidden"} min-h-0 flex-1 flex-col ${beside ? "xl:flex" : ""}`;
  const tab = beside ? "h-7 px-3 text-[11px]" : "h-11 px-3.5 text-[13px]";
  // Alone over the desk a tab sits on the canvas, where the shell's dim text cannot be read.
  const idle = beside ? "text-shell-dim" : "text-shell-lit";
  const musicxml = result?.musicxml ?? null;

  return (
    <div
      className={`flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto p-2 ${
        beside ? "xl:flex-row xl:overflow-hidden" : ""
      }`}
    >
      <div
        role="tablist"
        aria-label="Desk"
        className={`flex shrink-0 gap-1 ${beside ? "xl:hidden" : ""}`}
      >
        {PANELS.map((desk) => (
          <button
            key={desk}
            type="button"
            role="tab"
            aria-selected={desk === front}
            onClick={() => setChosen(desk)}
            className={`rounded ${tab} ${
              desk === front
                ? "bg-shell-key text-shell-lit"
                : `${idle} hover:bg-shell-hot hover:text-shell-lit`
            }`}
          >
            {desk === "original" ? "Original" : "Arrangement"}
          </button>
        ))}
      </div>

      <div className={shown("original")}>
        {source === null && fetching ? (
          <Empty>Fetching {wanted} from the library…</Empty>
        ) : source === null ? (
          <Empty>
            Open a MusicXML or MIDI score, or a PDF or photos of printed music, to begin. Its bars
            are drawn here as they were written, before anything is arranged, so you can see that
            the right file and the right window went in.
          </Empty>
        ) : "reason" in source ? (
          <Empty>
            This browser could not draw the file you picked: {source.reason}. The arranger reads it
            with its own parser, so a run may still work.
          </Empty>
        ) : (
          <ScoreAudition
            score={source.score}
            musicxml={source.musicxml}
            fileName={source.fileName}
            heading={`The original, bars ${source.bars}`}
            stage={originalStage}
            spaceKey={spaceTo === "original"}
            note={
              result === null
                ? "These bars as they were written. Play them now to check the file before you arrange them."
                : "The same bars as they were written, cut out of the file you picked and played by the same sampler, so the difference you hear is the arrangement and not the instrument."
            }
          />
        )}
      </div>

      <div className={shown("arranged")}>
        {result === null ? (
          <Empty>{waiting}</Empty>
        ) : read.score !== null && arranged !== null && musicxml !== null ? (
          <ScoreAudition
            score={arranged}
            sound={read.score}
            musicxml={musicxml}
            fileName={result.fileName}
            // Counted in its own bars, not the source's: a source that changes metre is rebarred.
            heading={
              arranged === read.score
                ? "The arrangement"
                : `The arrangement, its first ${DRAWN_BARS} of ${count(read.score.measures.length)} bars`
            }
            stage={arrangedStage}
            spaceKey={spaceTo === "arranged"}
            note={`Played by the editor's own sampler, one voice per seat: it is the notes and the rhythm, not the sound of the players.${arranged === read.score ? "" : " Every download and the editor get every bar."}`}
            onOpenInEditor={() => onOpenInEditor(musicxml)}
          />
        ) : (
          <Empty>
            The arrangement was written but this browser could not draw it:{" "}
            {read.reason ?? "it has no bars"}
          </Empty>
        )}
      </div>
    </div>
  );
}
